import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Snackbar, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  MaterialReactTable,
} from 'material-react-table';

import ComparisonView from './ComparisonView';
import { logger } from '../utils/logger';
import { useBulkSendStaging } from '../context/BulkSendStagingContext.jsx';
import useApi from '../hooks/useApi';
import useProductSelectionsActions from '../hooks/product-selections/useProductSelectionsActions';
import useProductSelectionsDi from '../hooks/product-selections/useProductSelectionsDi';
import useProductSelectionsExport from '../hooks/product-selections/useProductSelectionsExport';
import useProductSelectionsMrtTable from '../hooks/product-selections/useProductSelectionsMrtTable';
import useProductSelectionsRefresh from '../hooks/product-selections/useProductSelectionsRefresh';
import useProductSelectionsSync from '../hooks/product-selections/useProductSelectionsSync';
import useProductSelectionsTableState from '../hooks/product-selections/useProductSelectionsTableState';
import { resolveSelectionForStaging } from '../utils/productSelectionsRow';
import ProductSelectionsHeader from './product-selections/ProductSelectionsHeader';
import SyncSelectionsDialog from './product-selections/SyncSelectionsDialog';
import SendActionsMenu from './product-selections/SendActionsMenu';
import SendNotesDialog from './product-selections/SendNotesDialog';
import QuickActionDialogs from './product-selections/QuickActionDialogs';
import {
  buildLegacyColumns,
  buildSendToStagingColumn,
} from './product-selections/columns';

const ProductSelectionsTable = ({
  rows,
  isMaximized = false,
  enableInternalScroll = false,
  minTableHeight = 0,
  tableContainerTestId = null,
  onToggleMaximize = null,
  onNavigateToDealer,
  userEmail,
  onOpenDetail = () => {},
  additionalColumns = [],
  onRowClick: externalOnRowClick = undefined,
  additionalColumnsPlacement = 'end',
  columnsTransform = null,
  selectionModel: controlledSelectionModel = null,
  onSelectionModelChange = undefined,
  initialFilterModel = null, // best-effort: we map item.field/value to column filters
  initialPageSize = null,
  refreshProgramName = null,
  enableVmtQuickActions = false,
  onNotify = null,
  syncRefreshMode = 'threshold',
  storageKeySuffix = '',
  hideRefreshSelections = false,
  hideCompare = false,
  hideExportVisibleCsv = false,
  hideExportVmtTemplate = false,
  hideGlobalSearch = false,
  hideGlobalReset = false,
  hideMaximize = false,
}) => {
  const { stageRecord, stageRecords } = useBulkSendStaging();
  const {
    useDiOrderItemsByReferenceId,
    useVmtSend,
    useForceProgramSync,
    useRefreshDealerSelections,
  } = useApi();
  const { mutateAsync: fetchDiOrderItemsByReferenceId } = useDiOrderItemsByReferenceId();
  const sendMutation = useVmtSend();
  const forceProgramSyncMutation = useForceProgramSync();
  const { mutateAsync: refreshDealerSelections } = useRefreshDealerSelections();
  const isVitest =
    typeof import.meta !== 'undefined' &&
    import.meta?.env &&
    (import.meta.env.MODE === 'test' || Boolean(import.meta.env.VITEST));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isCompareViewOpen, setIsCompareViewOpen] = useState(false);

  const [internalMaximized, setInternalMaximized] = useState(false);
  const maximizeActive = typeof onToggleMaximize === 'function' ? Boolean(isMaximized) : internalMaximized;
  const handleIsFullScreenChange = useCallback(
    (updater) => {
      const next =
        typeof updater === 'function'
          ? updater(maximizeActive)
          : Boolean(updater);
      if (typeof onToggleMaximize === 'function') {
        if (next !== maximizeActive) {
          onToggleMaximize();
        }
        return;
      }
      setInternalMaximized(next);
    },
    [maximizeActive, onToggleMaximize]
  );

  const isSelectionControlled = Array.isArray(controlledSelectionModel);
  const [internalSelectionModel, setInternalSelectionModel] = useState([]);
  const selectionModel = isSelectionControlled ? controlledSelectionModel : internalSelectionModel;

  const handleSelectionModelChange = useCallback(
    (next) => {
      if (!isSelectionControlled) setInternalSelectionModel(next);
      onSelectionModelChange?.(next);
    },
    [isSelectionControlled, onSelectionModelChange]
  );

  const {
    refreshSelectionsLabel,
    refreshSelectionsDisabled,
    refreshSelectionsPending,
    handleRefreshSelections,
  } = useProductSelectionsRefresh({
    refreshProgramName,
    forceProgramSyncMutation,
    setSnackbar,
  });

  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    columnPinning,
    setColumnPinning,
    globalFilter,
    setGlobalFilter,
    columnFilters,
    setColumnFilters,
    sorting,
    setSorting,
    tableInitialState,
  } = useProductSelectionsTableState({ userEmail, initialFilterModel, initialPageSize, storageKeySuffix });

  const rowCount = Array.isArray(rows) ? rows.length : 0;

  useEffect(() => {
    logger.info('ProductSelectionsTable (MRT) data update', {
      rows: rowCount,
      isMaximized,
    });
  }, [rowCount, isMaximized]);

  const handleDiError = useCallback(
    (error) => {
      setSnackbar({
        open: true,
        severity: 'warning',
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to load DI fields (AppSheet cache unavailable).',
      });
    },
    [setSnackbar]
  );

  const { diEnrichedRows } = useProductSelectionsDi({
    rows,
    fetchDiOrderItemsByReferenceId,
    onError: handleDiError,
  });

  const enrichedRows = useMemo(() => diEnrichedRows || [], [diEnrichedRows]);

  const rowSelection = useMemo(() => {
    const next = {};
    (selectionModel || []).forEach((id) => {
      if (id != null) next[String(id)] = true;
    });
    return next;
  }, [selectionModel]);

  const selectedRows = useMemo(() => {
    const idSet = new Set((selectionModel || []).map((id) => String(id)));
    return enrichedRows.filter((r) => idSet.has(String(r?.id)));
  }, [enrichedRows, selectionModel]);

  const selectedDealers = useMemo(() => {
    const map = new Map();
    (selectedRows || []).forEach((row) => {
      const source = row.sourceEnrollment || row;
      const bac = source?.bac;
      if (!bac) return;
      if (!map.has(bac)) {
        map.set(bac, { ...source, productSelections: source.productSelections || [] });
      }
    });
    return Array.from(map.values());
  }, [selectedRows]);

  const compareDisabled = selectedDealers.length < 2;
  const buildVmtContextFromRow = useCallback((row) => {
    const enrollment = row.sourceEnrollment || row.source || row;
    return {
      bac: enrollment?.bac ?? row?.bac ?? '',
      programName: enrollment?.programName || row?.programName || row?.programLabel || '',
      programCode: enrollment?.programCode || row?.programCode || '',
      status:
        enrollment?.productSelectionStatus ||
        enrollment?.productSelectionStatusCd ||
        enrollment?.status ||
        row?.productSelectionStatus ||
        row?.status,
      designation: enrollment?.designation || row?.designation,
      type: enrollment?.type || row?.type,
      vendors: enrollment?.vendors || row?.vendors || [],
      brandCodes: enrollment?.brandCodes || row?.brandCodes || [],
      brandNames: enrollment?.brandNames || row?.brandNames || [],
      brandCd:
        enrollment?.gmBrandCd ||
        enrollment?.brandCd ||
        row?.gmBrandCd ||
        row?.brandCd ||
        enrollment?.brandCode ||
        row?.brandCode ||
        '',
      brandCode: enrollment?.brandCode || row?.brandCode || '',
      countryCd: enrollment?.countryCd || row?.countryCd || enrollment?.countryCode || row?.countryCode || '',
      lastUpdatedBy: enrollment?.lastUpdatedBy || row?.lastUpdatedBy || '',
    };
  }, []);
  const {
    sendMenu,
    notesModal,
    quickActionDialog,
    quickActionReferenceId,
    quickActionDate,
    handleSendMenuOpen,
    handleSendMenuClose,
    handleMenuSendDirect,
    handleMenuSendWithNotes,
    handleMenuQuickAction,
    handleOpenPopcornPrompt,
    handleOpenSetDatesPrompt,
    handleNotesChange,
    handleNotesCancel,
    handleNotesSubmit,
    handleQuickActionReferenceIdChange,
    handleQuickActionDateChange,
    closeQuickActionDialog,
    handleSubmitPopcorn,
    handleSubmitSetDates,
    handleBulkStageSelected,
  } = useProductSelectionsActions({
    selectedRows,
    buildVmtContextFromRow,
    stageRecord,
    stageRecords,
    sendMutation,
    userEmail,
    onNotify,
    setSnackbar,
  });

  const handleResetFilters = useCallback(
    (tableInstance) => {
      setGlobalFilter('');
      setColumnFilters([]);
      try {
        tableInstance?.setColumnFilters?.([]);
        tableInstance?.setGlobalFilter?.('');
        tableInstance?.resetColumnFilters?.();
      } catch (_err) {
        // noop
      }
    },
    [setColumnFilters, setGlobalFilter]
  );

  const handleGlobalFilterChange = useCallback(
    (value) => {
      setGlobalFilter(value ?? '');
    },
    [setGlobalFilter]
  );

  const sendToStagingColumn = useMemo(
    () =>
      buildSendToStagingColumn({
        onSendMenuOpen: handleSendMenuOpen,
      }),
    [handleSendMenuOpen]
  );

  const baseColumnsLegacy = useMemo(() => buildLegacyColumns(), []);

  const legacyColumns = useMemo(() => {
    const extras = Array.isArray(additionalColumns) ? additionalColumns : [];
    const seq = additionalColumnsPlacement === 'start' ? [...extras, ...baseColumnsLegacy] : [...baseColumnsLegacy, ...extras];
    const transformed = typeof columnsTransform === 'function' ? columnsTransform(seq) : seq;
    return Array.isArray(transformed) ? transformed : seq;
  }, [additionalColumns, additionalColumnsPlacement, baseColumnsLegacy, columnsTransform]);

  const { table, containerSx, tableWrapperSx } = useProductSelectionsMrtTable({
    legacyColumns,
    sendToStagingColumn,
    enrichedRows,
    externalOnRowClick,
    onOpenDetail,
    onNavigateToDealer,
    rowSelection,
    handleSelectionModelChange,
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    columnPinning,
    setColumnPinning,
    sorting,
    setSorting,
    globalFilter,
    handleGlobalFilterChange,
    columnFilters,
    setColumnFilters,
    tableInitialState,
    maximizeActive,
    handleIsFullScreenChange,
    handleResetFilters,
    enableInternalScroll,
    isMobile,
    isVitest,
    minTableHeight,
    tableContainerTestId,
    hideMaximize,
  });

  const {
    syncModalOpen,
    setSyncModalOpen,
    syncRows,
    syncQueue,
    syncProgress,
    syncSending,
    syncCounts,
    handleSyncSelections,
  } = useProductSelectionsSync({
    selectedRows,
    buildVmtContextFromRow,
    sendMutation,
    userEmail,
    syncRefreshMode,
    onNotify,
    refreshDealerSelections,
    forceProgramSyncMutation,
    setSnackbar,
  });

  const {
    handleCopySelected,
    handleExportVisibleCsv,
    handleExportSelectedCsv,
    handleExportVmtTemplate,
  } = useProductSelectionsExport({
    table,
    selectedRows,
    setSnackbar,
    buildVmtContextFromRow,
    resolveSelectionForStaging,
  });

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));
  return (
    <Box sx={containerSx} data-testid="data-grid">
      <ProductSelectionsHeader
        rowCount={enrichedRows.length}
        globalFilter={globalFilter}
        onGlobalFilterChange={(value) => table.setGlobalFilter?.(value ?? '')}
        onResetFilters={() => handleResetFilters(table)}
        refreshSelectionsLabel={refreshSelectionsLabel}
        refreshSelectionsDisabled={refreshSelectionsDisabled}
        refreshSelectionsPending={refreshSelectionsPending}
        onRefreshSelections={handleRefreshSelections}
        onOpenCompare={() => setIsCompareViewOpen(true)}
        compareDisabled={compareDisabled}
        selectedRowsCount={selectedRows.length}
        onCopySelectedTsv={() => void handleCopySelected('tsv')}
        onCopySelectedJson={() => void handleCopySelected('json')}
        onStageSelected={handleBulkStageSelected}
        onSyncSelected={handleSyncSelections}
        syncDisabled={!selectedRows.length || syncSending || syncQueue.length > 0}
        onExportVisibleCsv={() => void handleExportVisibleCsv()}
        onExportSelectedCsv={() => void handleExportSelectedCsv()}
        onExportVmtTemplate={() => void handleExportVmtTemplate()}
        hideRefreshSelections={hideRefreshSelections}
        hideCompare={hideCompare}
        hideExportVisibleCsv={hideExportVisibleCsv}
        hideExportVmtTemplate={hideExportVmtTemplate}
        hideGlobalSearch={hideGlobalSearch}
        hideGlobalReset={hideGlobalReset}
      />

      <Box sx={tableWrapperSx}>
        <MaterialReactTable table={table} />
      </Box>

      <ComparisonView open={isCompareViewOpen} onClose={() => setIsCompareViewOpen(false)} dealers={selectedDealers} />

      <SyncSelectionsDialog
        open={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        isMobile={isMobile}
        syncCounts={syncCounts}
        syncQueueLength={syncQueue.length}
        syncSending={syncSending}
        syncProgress={syncProgress}
        syncRows={syncRows}
        onClearSelections={() => handleSelectionModelChange([])}
        onSelectFailedRows={(ids) => handleSelectionModelChange(ids)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3200}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <SendActionsMenu
        anchorEl={sendMenu.anchorEl}
        row={sendMenu.row}
        onClose={handleSendMenuClose}
        onSendDirect={handleMenuSendDirect}
        onSendWithNotes={handleMenuSendWithNotes}
        enableVmtQuickActions={enableVmtQuickActions}
        onQuickAction={handleMenuQuickAction}
        onOpenPopcorn={handleOpenPopcornPrompt}
        onOpenSetDates={handleOpenSetDatesPrompt}
      />

      <SendNotesDialog
        open={notesModal.open}
        notes={notesModal.notes}
        onNotesChange={handleNotesChange}
        onCancel={handleNotesCancel}
        onSubmit={handleNotesSubmit}
      />

      <QuickActionDialogs
        quickActionType={quickActionDialog.type}
        quickActionReferenceId={quickActionReferenceId}
        quickActionDate={quickActionDate}
        onReferenceIdChange={handleQuickActionReferenceIdChange}
        onDateChange={handleQuickActionDateChange}
        onClose={closeQuickActionDialog}
        onSubmitPopcorn={handleSubmitPopcorn}
        onSubmitSetDates={handleSubmitSetDates}
      />
    </Box>
  );
};

export default ProductSelectionsTable;
